/**
 * Errors raised by the Vite dev server client or the React Refresh runtime.
 *
 * These modules are only served by `vite dev`, so a frame pointing at them can
 * never appear in a production build. They report on the state of the local HMR
 * session (socket drops, refreshing a component whose hooks changed shape)
 * rather than on the application itself.
 */
const DEV_TOOLING_MODULES = ['@vite/client', '@react-refresh'];

/** True when any stack frame filename points at the Vite dev client or React Refresh runtime. */
export function hasDevToolingFrame(filenames: readonly (string | undefined)[]): boolean {
  return filenames.some(
    (filename) =>
      filename != null && DEV_TOOLING_MODULES.some((module) => filename.includes(module)),
  );
}

const FRAME_LOCATION_SUFFIX = /:\d+:\d+$/;
const COMPACT_DEV_TOOLING_PATH = /\/(?:@vite\/client|@react-refresh):\d+:\d+/;

function stripFrameLocation(token: string): string {
  return token.replace(FRAME_LOCATION_SUFFIX, '');
}

/** Pull path-like tokens from a V8-style stack so match rules stay in one place. */
function filenamesFromStack(stack: string): string[] {
  const filenames: string[] = [];
  for (const raw of stack.split('\n')) {
    const line = raw.trim();
    const open = line.lastIndexOf('(');
    const close = line.lastIndexOf(')');
    if (open !== -1 && close > open) {
      filenames.push(stripFrameLocation(line.slice(open + 1, close)));
      continue;
    }
    if (line.startsWith('at ')) {
      filenames.push(stripFrameLocation(line.slice(3).trim()));
      continue;
    }
    const compact = COMPACT_DEV_TOOLING_PATH.exec(line);
    if (compact) {
      filenames.push(stripFrameLocation(compact[0]));
    }
  }
  return filenames;
}

/** True when `error` was thrown from the Vite dev client or React Refresh runtime. */
export function isDevToolingNoise(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return hasDevToolingFrame(filenamesFromStack(error.stack || ''));
}
